import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axiosInstance from "../../api/axiosConfig";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const OPEN_FOR_MARKS = "OPEN_FOR_MARKS";

const TeacherExamMarksEntry = () => {
  const schoolId = localStorage.getItem("schoolId");
  const teacherId = localStorage.getItem("userId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [examTypes, setExamTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});

  const [selectedExamTypeId, setSelectedExamTypeId] = useState("");
  const [selectedSubjectKey, setSelectedSubjectKey] = useState("");

  useEffect(() => {
    const loadBase = async () => {
      if (!schoolId || !teacherId) {
        setError("Missing school or teacher context. Please login again.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [examRes, subjectRes] = await Promise.all([
          axiosInstance.get(`/api/examination-types/school/${schoolId}`),
          axiosInstance.get(`/api/subject-teacher-assignments/teacher/${teacherId}/school/${schoolId}`),
        ]);

        const openExams = (examRes.data || []).filter(
          (item) => String(item?.status || "").toUpperCase() === OPEN_FOR_MARKS
        );
        setExamTypes(openExams);
        setSubjects(subjectRes.data || []);
      } catch (e) {
        setError("Failed to load exams/subjects.");
      } finally {
        setLoading(false);
      }
    };
    loadBase();
  }, [schoolId, teacherId]);

  const selectedSubject = useMemo(() => {
    return subjects.find(
      (item) =>
        `${item.subjectId}_${item.classId}_${item.sectionId}` === selectedSubjectKey
    );
  }, [selectedSubjectKey, subjects]);

  const selectedExam = useMemo(
    () => examTypes.find((item) => String(item.examinationTypeId) === String(selectedExamTypeId)),
    [examTypes, selectedExamTypeId]
  );

  const loadStudents = async () => {
    setError("");
    setSuccess("");
    setStudents([]);
    setMarks({});
    if (!selectedExamTypeId || !selectedSubject) {
      setError("Select exam type and subject.");
      return;
    }
    try {
      setLoading(true);
      const res = await axiosInstance.get("/api/students/exam-results", {
        params: {
          examinationTypeId: selectedExamTypeId,
          classId: selectedSubject.classId,
          sectionId: selectedSubject.sectionId,
          subjectId: selectedSubject.subjectId,
        },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setStudents(rows);

      const initialMarks = {};
      rows.forEach((student) => {
        const subjectResult = (student.subjectResults || []).find(
          (s) => String(s.subjectId) === String(selectedSubject.subjectId)
        );
        initialMarks[student.studentId] =
          subjectResult?.marksObtained == null ? "-1" : String(subjectResult.marksObtained);
      });
      setMarks(initialMarks);
    } catch (e) {
      setError("Failed to load students for selected exam/subject.");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkChange = (studentId, value) => {
    if (value === "" || value === "-1" || /^-?\d*\.?\d*$/.test(value)) {
      setMarks((prev) => ({ ...prev, [studentId]: value }));
    }
  };

  const submitMarks = async () => {
    setError("");
    setSuccess("");
    if (!selectedExamTypeId || !selectedSubject || !students.length) {
      setError("Load students before submitting marks.");
      return;
    }

    const invalid = students.find((student) => {
      const raw = String(marks[student.studentId] ?? "").trim();
      if (!raw) return true;
      if (raw === "-1") return false;
      const num = Number(raw);
      return Number.isNaN(num) || num < 0;
    });
    if (invalid) {
      setError("All students must have valid marks or -1 (absent).");
      return;
    }

    const payload = students.map((student) => {
      const raw = String(marks[student.studentId]).trim();
      const subjectResult = (student.subjectResults || []).find(
        (s) => String(s.subjectId) === String(selectedSubject.subjectId)
      );
      const maxMarks = Number(subjectResult?.maxMarks ?? 100);
      const passMarks = Number(subjectResult?.passMarks ?? 35);
      return {
        schoolId: Number(schoolId),
        studentId: Number(student.studentId),
        subjectId: Number(selectedSubject.subjectId),
        subjectName: selectedSubject.subjectName,
        marksObtained: raw === "-1" ? null : Number(raw),
        examinationTypeId: Number(selectedExamTypeId),
        classId: Number(selectedSubject.classId),
        sectionId: Number(selectedSubject.sectionId),
        maxMarks,
        passMarks,
      };
    });

    try {
      setSaving(true);
      await axiosInstance.post("/api/results/bulk", payload);
      setSuccess("Marks submitted successfully.");
    } catch (e) {
      setError("Failed to submit marks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Submit Exam Results"
        subtitle="Hybrid parity flow: select exam + subject, enter marks, submit in bulk."
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Examination Type"
              value={selectedExamTypeId}
              onChange={(e) => setSelectedExamTypeId(e.target.value)}
              size="small"
            >
              {examTypes.map((exam) => (
                <MenuItem key={exam.examinationTypeId} value={exam.examinationTypeId}>
                  {exam.examinationTypeName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Assigned Subject (Class/Section)"
              value={selectedSubjectKey}
              onChange={(e) => setSelectedSubjectKey(e.target.value)}
              size="small"
            >
              {subjects.map((subject) => {
                const key = `${subject.subjectId}_${subject.classId}_${subject.sectionId}`;
                return (
                  <MenuItem key={key} value={key}>
                    {subject.subjectName} - {subject.className} / {subject.sectionName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={loadStudents} disabled={loading}>
              Load
            </Button>
          </Grid>
        </Grid>

        {selectedExam && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Status: {selectedExam.status}
          </Typography>
        )}

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert> : null}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : (
          <TableContainer sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Admission No</TableCell>
                  <TableCell>Marks (-1 = Absent)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No students loaded.
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student) => (
                    <TableRow key={student.studentId}>
                      <TableCell>{student.studentName || "-"}</TableCell>
                      <TableCell>{student.admissionNumber || "-"}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={marks[student.studentId] ?? ""}
                          onChange={(e) => handleMarkChange(student.studentId, e.target.value)}
                          sx={{ width: 180 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={submitMarks}
            disabled={saving || loading || students.length === 0}
          >
            {saving ? "Submitting..." : "Submit Results"}
          </Button>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default TeacherExamMarksEntry;
